import {Inject, Injectable, InjectionToken, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  fromEvent,
  of,
  timer,
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {AppState} from '../states/app.state';
import {
  VersandstatusPollingActions,
  VersandstatusPollingScope,
} from '../actions/versandstatus-polling.actions';
import {FakturierungsbelegEntitiesActions} from '../actions/fakturierungsbeleg-entities.actions';
import {FakturierungsbelegTableActions} from '../actions/fakturierungsbeleg-table.actions';
import {FakturierungsbelegEntitiesSelectors} from '../selectors/fakturierungsbeleg-entities.selectors';
import {FakturierungsbelegTableSelectors} from '../selectors/fakturierungsbeleg-table.selectors';
import {VERSANDSTATUS_POLLING_CONFIG} from '../../util/versandstatus-polling.config';
import {getVersandstatusTyp, isOffen} from '../../util/versandstatus.util';
import {BelegDTO, PageableDTO, VersandStatusDTO} from '../../openapi/fakturierung-openapi';
import {ScopeRuntime} from './scope-runtime.interface';


/**
 * InjectionToken fuer den Visibility-Stream. Wird vom Polling-Effect
 * konsumiert; in Tests via `{provide: VISIBILITY_STREAM, useValue: ...}`
 * deterministisch mockbar.
 */
export const VISIBILITY_STREAM = new InjectionToken<Observable<boolean>>(
  'VERSANDSTATUS_POLLING_VISIBILITY_STREAM',
);


/**
 * Default-Factory fuer den Visibility-Stream — emittiert `true`, wenn das
 * Browser-Tab sichtbar ist. {@code shareReplay({bufferSize: 1, refCount: true})}
 * stellt sicher, dass alle Scope-Subscriptions denselben aktuellen Wert sehen.
 */
export function visibilityStreamFactory(): Observable<boolean> {
  return fromEvent(document, 'visibilitychange').pipe(
    startWith(null),
    map(() => document.visibilityState === 'visible'),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: true}),
  );
}


/**
 * Kapselt das Versandstatus-Polling fuer Belegliste und Beleg-Preview.
 *
 * Pro Scope (`list` | `preview`) laeuft maximal ein Polling-Stream:
 *  - reagiert auf `startPolling` mit Aufbau eines `interval(intervalMs)`-Streams
 *  - pausiert bei `document.hidden` und resumed bei `visibilitychange`
 *  - Circuit-Breaker: Nach `failureThreshold` Fehlern wird das Polling fuer
 *    einen exponentiellen Cooldown pausiert. Counter wird NACH dem Cooldown
 *    zurueckgesetzt — nicht beim ersten Success-Roundtrip.
 *  - bei `stopPolling` wird der gesamte Lifecycle fuer den Scope beendet.
 *
 * Die Komponenten dispatchen lediglich `startPolling` / `stopPolling`. Der
 * Effect uebersetzt Ticks in `readBelege` (List) bzw. `getFakturierungsbelegById`
 * (Preview) und beobachtet die zugehoerigen Success-/Failure-Actions fuer
 * den Failure-Counter.
 */
@Injectable()
export class VersandstatusPollingEffects implements OnDestroy {

  private readonly _scopes = new Map<VersandstatusPollingScope, ScopeRuntime>();

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    @Inject(VISIBILITY_STREAM) private readonly _visibility$: Observable<boolean>,
  ) {
    this.setupFailureSuccessTracking();
  }

  /**
   * Reagiert auf `startPolling` und initialisiert/erneuert den Polling-Stream
   * fuer den angegebenen Scope.
   */
  readonly start$ = createEffect(
    () => this.actions$.pipe(
      ofType(VersandstatusPollingActions.startPolling),
      tap(({scope, betriebId, belegId}) => this.startScope(scope, betriebId, belegId)),
    ),
    {dispatch: false},
  );

  /**
   * Reagiert auf `stopPolling` und tear-down des Scope-Lifecycles.
   */
  readonly stop$ = createEffect(
    () => this.actions$.pipe(
      ofType(VersandstatusPollingActions.stopPolling),
      tap(({scope}) => this.stopScope(scope)),
    ),
    {dispatch: false},
  );

  /**
   * Uebersetzt einen Polling-Tick in die scope-spezifische Read-Action.
   * Pre-Read-Bedingungen (Tab sichtbar, OFFEN-Belege vorhanden, nicht
   * pausiert) werden im Tick-Stream selbst geprueft — hier nur noch der
   * Read-Dispatch.
   */
  readonly tick$ = createEffect(
    () => this.actions$.pipe(
      ofType(VersandstatusPollingActions.pollingTick),
      switchMap(({scope, betriebId, belegId}) => {
        const runtime = this._scopes.get(scope);
        if (!runtime) return EMPTY;
        runtime.pollInFlight = true;

        if (scope === 'preview' && belegId) {
          return of(FakturierungsbelegEntitiesActions.getFakturierungsbelegById({
            betriebId,
            belegId,
          }));
        }

        if (scope === 'list') {
          return this.store.select(FakturierungsbelegTableSelectors.pageableDto).pipe(
            take(1),
            map(pageableDto => FakturierungsbelegTableActions.readFakturierungsbelege({
              betriebId,
              pageableDto: pageableDto ?? ({} as PageableDTO),
            })),
          );
        }

        return EMPTY;
      }),
    ),
  );

  ngOnDestroy(): void {
    for (const scope of Array.from(this._scopes.keys())) {
      this.stopScope(scope);
    }
  }

  private startScope(
    scope: VersandstatusPollingScope,
    betriebId: string,
    belegId: string | undefined,
  ): void {
    // Bestehenden Scope tear-down, falls vorhanden — z.B. bei
    // BetriebId/BelegId-Wechsel innerhalb derselben Component.
    this.stopScope(scope);

    const runtime: ScopeRuntime = {
      stop$: new Subject<void>(),
      paused$: new BehaviorSubject<boolean>(false),
      consecutiveFailures: 0,
      cooldownAttempts: 0,
      pollInFlight: false,
      betriebId,
      belegId,
    };
    this._scopes.set(scope, runtime);

    const trigger$ = scope === 'preview'
      ? this.buildPreviewTrigger$(belegId)
      : this.buildListTrigger$();

    combineLatest([
      trigger$,
      this._visibility$,
      runtime.paused$,
    ]).pipe(
      switchMap(([active, visible, paused]) => {
        if (!active || !visible || paused) {
          return EMPTY;
        }
        // Erstes Poll nach initialDelayMs, dann alle intervalMs.
        // take(maxAttemptsWithoutChange) deckelt im "kein-Status-Wechsel"-Fall.
        return timer(
          VERSANDSTATUS_POLLING_CONFIG.initialDelayMs,
          VERSANDSTATUS_POLLING_CONFIG.intervalMs,
        ).pipe(
          take(VERSANDSTATUS_POLLING_CONFIG.maxAttemptsWithoutChange),
        );
      }),
      takeUntil(runtime.stop$),
    ).subscribe(() => {
      this.store.dispatch(VersandstatusPollingActions.pollingTick({
        scope,
        betriebId: runtime.betriebId!,
        belegId: runtime.belegId,
      }));
    });
  }

  private stopScope(scope: VersandstatusPollingScope): void {
    const runtime = this._scopes.get(scope);
    if (!runtime) return;
    runtime.stop$.next();
    runtime.stop$.complete();
    runtime.paused$.complete();
    runtime.cooldownSub?.unsubscribe();
    this._scopes.delete(scope);
  }

  /**
   * Aktivierungs-Trigger fuer die Preview: emittiert `true`, sobald der
   * Beleg im Status OFFEN ist; `false` sobald er nicht mehr OFFEN ist.
   * `distinctUntilChanged` verhindert Timer-Reset bei reinen Objekt-
   * Replacements im Store.
   */
  private buildPreviewTrigger$(belegId: string | undefined): Observable<boolean> {
    if (!belegId) return of(false);

    return this.store.select(FakturierungsbelegEntitiesSelectors.belegById(belegId)).pipe(
      map(beleg => isOffen(beleg?.aktuellerVersandstatus?.status as VersandStatusDTO | undefined)),
      distinctUntilChanged(),
    );
  }

  /**
   * Aktivierungs-Trigger fuer die Belegliste: emittiert `true`, solange
   * mindestens ein angezeigter Beleg den Versandstatus OFFEN hat. Zusaetzlich
   * wird der Snapshot der Versandstatus-Strings beobachtet, sodass jede
   * fachliche Aenderung den Timer durch `switchMap` reset.
   */
  private buildListTrigger$(): Observable<boolean> {
    const displayed$ = this.store.select(FakturierungsbelegTableSelectors.displayedFakturierungsbelege);

    const hasOpen$ = displayed$.pipe(
      map((belege: BelegDTO[]) => belege.some(beleg => isOffen(beleg.aktuellerVersandstatus?.status as VersandStatusDTO | undefined))),
      distinctUntilChanged(),
    );

    const snapshot$ = displayed$.pipe(
      map((belege: BelegDTO[]) => belege
        .map(beleg => `${beleg.id}:${getVersandstatusTyp(beleg.aktuellerVersandstatus?.status as VersandStatusDTO | undefined) ?? '-'}`)
        .sort()
        .join('|')),
      distinctUntilChanged(),
    );

    return combineLatest([hasOpen$, snapshot$]).pipe(
      map(([hasOpen]) => hasOpen),
    );
  }

  private setupFailureSuccessTracking(): void {
    // Preview-Failure / -Success
    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdFailure),
    ).subscribe(() => this.handleFailure('preview'));

    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdSuccess),
    ).subscribe(() => this.handleSuccess('preview'));

    // List-Failure / -Success
    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.readFakturierungsbelegeFailure),
    ).subscribe(() => this.handleFailure('list'));

    this.actions$.pipe(
      ofType(FakturierungsbelegEntitiesActions.readFakturierungsbelegeSuccess),
    ).subscribe(() => this.handleSuccess('list'));
  }

  private handleFailure(scope: VersandstatusPollingScope): void {
    const runtime = this._scopes.get(scope);
    if (!runtime) return;
    if (!runtime.pollInFlight) return;
    runtime.pollInFlight = false;
    runtime.consecutiveFailures++;
    if (runtime.consecutiveFailures >= VERSANDSTATUS_POLLING_CONFIG.failureThreshold) {
      this.enterCooldown(runtime);
    }
  }

  private handleSuccess(scope: VersandstatusPollingScope): void {
    const runtime = this._scopes.get(scope);
    if (!runtime) return;
    runtime.pollInFlight = false;
    runtime.consecutiveFailures = 0;
    if (runtime.paused$.value) {
      runtime.paused$.next(false);
    }
  }

  /**
   * Berechnet den Cooldown anhand von {@link cooldownAttempts}, pausiert
   * den Polling-Stream und setzt den Failure-Counter NACH Cooldown-Ablauf
   * zurueck (nicht sofort — sonst wuerde ein einzelner Erfolgstick die
   * Backoff-Eskalation rueckgaengig machen).
   */
  private enterCooldown(runtime: ScopeRuntime): void {
    runtime.paused$.next(true);
    const cooldownMs = this.computeBackoffMs(runtime.cooldownAttempts);
    runtime.cooldownAttempts++;

    runtime.cooldownSub?.unsubscribe();
    runtime.cooldownSub = timer(cooldownMs).pipe(
      takeUntil(runtime.stop$),
    ).subscribe(() => {
      runtime.consecutiveFailures = 0;
      if (runtime.paused$.value) {
        runtime.paused$.next(false);
      }
    });
  }

  /**
   * Exponential-Backoff mit Jitter:
   *   base = initialBackoffMs * backoffFactor^attempt
   *   jitter = base * jitterRatio * uniform(-1, +1)
   *   result = clamp(base + jitter, 0, maxBackoffMs)
   */
  private computeBackoffMs(attempt: number): number {
    const base = Math.min(
      VERSANDSTATUS_POLLING_CONFIG.initialBackoffMs * Math.pow(VERSANDSTATUS_POLLING_CONFIG.backoffFactor, attempt),
      VERSANDSTATUS_POLLING_CONFIG.maxBackoffMs,
    );
    const jitter = base * VERSANDSTATUS_POLLING_CONFIG.jitterRatio * (Math.random() * 2 - 1);
    return Math.max(0, Math.min(VERSANDSTATUS_POLLING_CONFIG.maxBackoffMs, base + jitter));
  }
}

